import { Sun, Moon, Monitor } from '@phosphor-icons/react';
import { useIntl } from 'react-intl';
import { useTheme } from '@/providers/ThemeProvider';

/**
 * ThemeToggle -- cycles through dark / light / system on click.
 *
 * Icons:
 * - Dark mode active  -> Sun icon  (meaning: "click to go light")
 * - Light mode active -> Moon icon (meaning: "click to go dark")
 * - System mode       -> Monitor icon
 *
 * Smooth 200ms icon transition. aria-label and title name the action the click performs (i18n).
 */

type ThemeToggleSize = 'sm' | 'md' | 'lg';

interface ThemeToggleProps {
  /** Button size */
  size?: ThemeToggleSize;
  /** Additional class names */
  className?: string;
}

const SIZE_MAP: Record<ThemeToggleSize, { button: string; icon: number }> = {
  sm: { button: 'h-8 w-8 min-h-[var(--touch-target-min)] min-w-[var(--touch-target-min)] lg:min-h-0 lg:min-w-0', icon: 16 },
  md: { button: 'h-9 w-9 min-h-[var(--touch-target-min)] min-w-[var(--touch-target-min)] lg:min-h-0 lg:min-w-0', icon: 20 },
  lg: { button: 'h-10 w-10 min-h-[var(--touch-target-min)] min-w-[var(--touch-target-min)]', icon: 24 },
};

/** i18n key of the action the click performs, per current theme preference. */
const ACTION_LABEL_IDS = {
  dark: 'common.theme.switchToLight',
  light: 'common.theme.switchToSystem',
  system: 'common.theme.switchToDark',
} as const;

export function ThemeToggle({ size = 'md', className = '' }: ThemeToggleProps) {
  const { theme, resolvedTheme, toggleTheme } = useTheme();
  const intl = useIntl();
  const actionLabel = intl.formatMessage({ id: ACTION_LABEL_IDS[theme] });

  const { button: buttonSize, icon: iconSize } = SIZE_MAP[size];

  // Determine which icon to show based on current theme preference
  const renderIcon = () => {
    if (theme === 'system') {
      return (
        <Monitor
          size={iconSize}
          weight="duotone"
          className="transition-all duration-200 ease-default"
        />
      );
    }
    if (resolvedTheme === 'dark') {
      return (
        <Sun
          size={iconSize}
          weight="duotone"
          className="transition-all duration-200 ease-default"
        />
      );
    }
    return (
      <Moon
        size={iconSize}
        weight="duotone"
        className="transition-all duration-200 ease-default"
      />
    );
  };

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={actionLabel}
      title={actionLabel}
      className={[
        // Layout
        'relative inline-flex items-center justify-center',
        buttonSize,
        // Appearance
        'rounded-radius-md',
        'text-text-secondary',
        'bg-transparent',
        'border border-border-subtle',
        // Hover / active
        'hover:bg-bg-hover hover:text-text-primary',
        'active:bg-bg-active',
        // Focus
        'focus-visible:outline-2 focus-visible:outline-border-focus focus-visible:outline-offset-2',
        // Transition
        'transition-colors duration-200 ease-default',
        // Cursor
        'cursor-pointer',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {renderIcon()}
    </button>
  );
}
